import React, { useState, useContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Save, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { UserService } from '../../services/userService';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';  

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const { user, logout, updateAuthContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from || '/studies';
  
  // Check if this is a first-time password change
  const isFirstTimeChange = user?.requires_password_change;

  useEffect(() => {
    // Log the state for debugging
    console.log('[CHANGE_PASSWORD] Component mounted with state:', { 
      isFirstTimeChange,
      redirectPath,
      user: user ? { 
        email: user.email, 
        requiresPasswordChange: user.requires_password_change 
      } : null
    });
  }, [isFirstTimeChange, redirectPath, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'current_password') {
      setCurrentPassword(value);
    } else if (name === 'new_password') {
      setNewPassword(value);
    } else if (name === 'confirm_password') {
      setConfirmPassword(value);
    }
    setError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    // Validation
    if (!isFirstTimeChange && !currentPassword) {
      setError('Current password is required');
      return;
    }
    
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    
    // Prepare data for submission
    const passwordData = {
      new_password: newPassword,
      confirm_password: confirmPassword,
    };
    
    // Only include current password if this is not a first-time change
    if (!isFirstTimeChange) {
      passwordData.current_password = currentPassword;
    }

    console.log('[CHANGE_PASSWORD] Submitting password change with:', { 
      isFirstTimeChange,
      hasCurrentPassword: !!passwordData.current_password,
      email: user?.email
    });
    
    setIsLoading(true);
    
    try {
      const response = await UserService.changePassword(passwordData);
      setSuccessMessage('Password changed successfully');
      
      // Update user in context to clear the requires_password_change flag
      if (user && isFirstTimeChange) {
        const updatedUser = { ...user, requires_password_change: false };
        updateAuthContext({ user: updatedUser });
      }
      
      console.log('[CHANGE_PASSWORD] Password changed successfully, redirecting to:', redirectPath);
      
      // Redirect after successful password change
      setTimeout(() => {
        navigate(redirectPath);
      }, 1500);
    } catch (error) {
      console.error('[CHANGE_PASSWORD] Error:', error);
      
      if (typeof error === 'string') {
        // Direct error message from UserService
        setError(error);
      } else if (error.response && error.response.data) {
        // Get the complete error data directly from the API response
        const errorData = error.response.data;

        // Store the entire error response for display
        setError(errorData);
      } else {
        // Generic error fallback
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Fields to display based on whether this is a first-time password change
  const passwordFields = isFirstTimeChange 
    ? ['new', 'confirm'] // Skip current password for first-time changes
    : ['current', 'new', 'confirm'];

  return (
    <div>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card overflow-hidden"
        >
          <div className="card-band">
            <h2 className="card-band-title flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {isFirstTimeChange ? 'Set New Password' : 'Change Password'}
            </h2>
          </div>

          {isFirstTimeChange && (
            <div className="px-6 pt-5 text-sm text-gray-600 dark:text-gray-300">
              <p>You must set a new password before continuing.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="rounded-lg border border-danger-500/25 bg-danger-50 px-4 py-3 text-sm dark:bg-danger-500/10"
              >
                <div className="flex gap-2.5">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-danger-600 dark:text-danger-400" />
                  <div className="w-full text-danger-700 dark:text-danger-400">
                    {typeof error === 'object' ? (
                      <div>
                        <p className="mb-1.5 font-medium">{error.error || 'Password validation failed'}</p>
                        {error.validation_errors && error.validation_errors.length > 0 ? (
                          <ul className="list-disc space-y-1 pl-5">
                            {error.validation_errors.map((validationError, i) => (
                              <li key={i}>{validationError}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>{error.message || 'Please try again with a different password.'}</p>
                        )}
                      </div>
                    ) : (
                      // For string error messages, just show directly
                      <p>{error}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="rounded-lg border border-success-500/25 bg-success-50 px-4 py-3 text-sm dark:bg-success-500/10"
              >
                <p className="font-medium text-success-700 dark:text-success-400">{successMessage}</p>
              </motion.div>
            )}

            {passwordFields.map((field) => (
              <div key={field} className="relative">
                <label className="label">
                  {field.charAt(0).toUpperCase() + field.slice(1)} Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords[field] ? 'text' : 'password'}
                    name={`${field}_password`}
                    value={field === 'current' ? currentPassword : field === 'new' ? newPassword : confirmPassword}
                    onChange={handleChange}
                    className="input pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility(field)}
                    aria-label={showPasswords[field] ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    {showPasswords[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}

            <div className="pt-1">
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary btn-lg w-full"
              >
                <Save className="h-4 w-4" />
                {isLoading ? 'Changing Password...' : 'Save Password'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ChangePassword;